import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MoreActionsMenuItem from '../../../../../MoreActionsMenu/MoreActionsMenuItem';
import { SelectOption } from 'basics/options/options.types';
import { MenuItemConfig } from 'components/MoreActionsMenu/MoreActionsMenu.types';
import { Tactic } from 'generated/maya.types';

type UseTacticStepControlPanelConfigParams = {
  actionOrder: string;
  availableSteps: string[];
  allGroupedTactics: Record<string, Tactic[]> | undefined;
  currentDependency: string | null;
  onReorder: (destinationStep: string) => void | Promise<void>;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const isLast = (index: number, arr: unknown[]) => index >= arr.length - 1;

const getMoveToMenuItemConfigs = (
  availableSteps: string[],
  onReorder: (destinationStep: string) => void | Promise<void>,
): MenuItemConfig[] => availableSteps.map((step) => ({
  id: step,
  label: `Step ${step}`,
  hideItem: false,
  onClick: async () => {
    await onReorder(step);
  },
}));

const getStepDependencyOptions = ({
  actionOrder,
  availableSteps,
  allGroupedTactics,
  currentDependency,
}: {
  actionOrder: string;
  availableSteps: string[];
  allGroupedTactics: Record<string, Tactic[]> | undefined;
  currentDependency: string | null;
}): SelectOption[] => {
  const currentStep = parseInt(actionOrder, 10);
  const grouped = allGroupedTactics ?? {};

  const previousSteps = availableSteps
    .filter((step) => parseInt(step, 10) < currentStep)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const currentStepTactics: Tactic[] = grouped[actionOrder] ?? [];
  const currentStepHasGroup = currentStepTactics.some((tactic) => !!tactic.parentTactic);

  return [
    ...(currentDependency ? [{ id: 'None', label: 'None' }] : []),
    ...previousSteps
      .filter((step) => {
        const stepTactics: Tactic[] = grouped[step] ?? [];
        const stepHasGroup = stepTactics.some((tactic) => !!tactic.parentTactic);

        return currentStepHasGroup ? stepHasGroup : true;
      })
      .map((step) => ({ id: step, label: `Step ${step}` })),
  ];
};

const useTacticStepControlPanelConfig = ({
  actionOrder,
  availableSteps,
  allGroupedTactics,
  currentDependency,
  onReorder,
  setOpen,
}: UseTacticStepControlPanelConfigParams) => {
  const { t } = useTranslation();

  const currentStep = useMemo(
    () => parseInt(actionOrder, 10),
    [actionOrder],
  );

  const menuItemConfigs = useMemo(
    () => getMoveToMenuItemConfigs(availableSteps, onReorder),
    [availableSteps, onReorder],
  );

  const menuItems = useMemo(
    () => menuItemConfigs.map((step, index) => (
      <MoreActionsMenuItem
        key={ step?.id }
        menuItemConfig={ step }
        isLast={ isLast(index, menuItemConfigs) }
        closeMenu={ () => setOpen(false) }
      />
    )),
    [menuItemConfigs, setOpen],
  );

  const selectOptions = useMemo(
    () => getStepDependencyOptions({
      actionOrder,
      availableSteps,
      allGroupedTactics,
      currentDependency,
    }),
    [
      actionOrder,
      availableSteps,
      allGroupedTactics,
      currentDependency,
    ],
  );

  const dependOnSelectorConfig = useMemo(
    () => ({
      name: `stepDependOnSelector_${actionOrder}`,
      placeholder: t('maya_tactic_depends_on_select_placeholder'),
      options: selectOptions,
      defaultValue: currentDependency || '',
      disabled: currentStep === 1,
    }),
    [
      actionOrder,
      currentDependency,
      currentStep,
      selectOptions,
      t,
    ],
  );

  return {
    currentStep,
    menuItems,
    dependOnSelectorConfig,
  };
};

export default useTacticStepControlPanelConfig;
